import { type Component, createMemo, createSignal, For, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import Checkbox from '~/components/Checkbox'
import DateInput from '~/components/DateInput'
import FieldMessage from '~/components/FieldMessage'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Radio from '~/components/Radio'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Textarea from '~/components/Textarea'
import SignaturePad from '~/layers/SignaturePad'
import { type FormFieldType, type FormSettingsType, type FormStepType, fieldsOfStep, isAnswerableField, normalizeSteps, WIDTH_SPAN } from '~/services/form.service'

export interface FormRendererPropsType {
  fields: FormFieldType[]
  steps?: FormStepType[] | null
  settings?: FormSettingsType | null
  /** Errores por campo que llegan del servidor tras un envío rechazado. */
  serverErrors?: Record<string, string>
  /** Mensaje general (p. ej. "Revisa los campos marcados"). */
  globalError?: string
  submitting?: boolean
  /**
   * En previsualización se puede recorrer y escribir, pero el botón final no
   * envía nada: sirve para ver el formulario antes de publicarlo.
   */
  preview?: boolean
  onSubmit?: (answers: Record<string, unknown>) => void
}

/**
 * Pinta un formulario a partir de su definición. Lo usan la página pública y la
 * previsualización del constructor, así que lo que se ve al diseñar es
 * exactamente lo que verá quien responda.
 */
const FormRenderer: Component<FormRendererPropsType> = props => {
  const [answers, setAnswers] = createStore<Record<string, unknown>>({})
  const [errors, setErrors] = createStore<Record<string, string>>({})
  const [stepIndex, setStepIndex] = createSignal(0)
  const [localError, setLocalError] = createSignal('')

  const steps = createMemo(() => normalizeSteps(props.steps))
  const currentStep = createMemo(() => steps()[Math.min(stepIndex(), steps().length - 1)])
  const isLastStep = createMemo(() => stepIndex() >= steps().length - 1)
  const stepFields = createMemo(() => fieldsOfStep(props.fields, currentStep()?.id ?? '', stepIndex() === 0))
  const hasAnswerable = createMemo(() => props.fields.some(isAnswerableField))

  /** El error de un campo puede venir de esta pantalla o del servidor. */
  const errorOf = (id: string) => errors[id] || props.serverErrors?.[id] || ''

  const setAnswer = (id: string, value: unknown) => {
    setAnswers(id, value)
    if (errors[id]) setErrors(id, '')
  }

  const toggleChoice = (id: string, option: string, checked: boolean) => {
    const current = Array.isArray(answers[id]) ? [...(answers[id] as string[])] : []
    setAnswer(id, checked ? [...current, option] : current.filter(value => value !== option))
  }

  /** Se valida lo obligatorio del paso antes de dejar avanzar o enviar. */
  const validateStep = () => {
    let ok = true
    for (const field of stepFields()) {
      if (!isAnswerableField(field) || !field.required) continue
      const value = answers[field.id]
      const empty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
      if (empty) {
        setErrors(field.id, 'Este campo es obligatorio')
        ok = false
      }
    }
    return ok
  }

  const goNext = () => {
    if (!validateStep()) {
      setLocalError('Completa los campos obligatorios para continuar.')
      return
    }
    setLocalError('')
    setStepIndex(index => Math.min(index + 1, steps().length - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const goBack = () => {
    setLocalError('')
    setStepIndex(index => Math.max(index - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = (event: SubmitEvent) => {
    event.preventDefault()

    // Pulsar Enter envía el formulario aunque falten pasos: ahí sólo avanzamos.
    if (!isLastStep()) {
      goNext()
      return
    }

    if (!validateStep()) {
      setLocalError('Completa los campos obligatorios.')
      return
    }

    setLocalError('')
    props.onSubmit?.({ ...answers })
  }

  const renderField = (field: FormFieldType) => {
    const error = () => errorOf(field.id)

    switch (field.type) {
      case 'heading':
        return <h2 class="text-lg font-bold text-foreground pt-2">{field.label}</h2>

      case 'paragraph':
        return <p class="text-sm text-foreground-subtle whitespace-pre-line">{field.label}</p>

      case 'divider':
        return <hr class="border-border my-2" />

      case 'spacer':
        return <div class="h-6" />

      case 'signature':
        return (
          <SignaturePad
            label={field.label}
            description={field.description}
            required={field.required}
            error={error()}
            value={(answers[field.id] as string) ?? ''}
            onChange={value => setAnswer(field.id, value)}
          />
        )

      case 'long_text':
        return (
          <Textarea
            label={field.label}
            rows={4}
            required={field.required}
            placeholder={field.placeholder}
            helperText={field.description}
            error={error()}
            value={(answers[field.id] as string) ?? ''}
            onInput={e => setAnswer(field.id, e.currentTarget.value)}
          />
        )

      case 'select':
        return (
          <div>
            <Select
              label={field.label}
              options={(field.options ?? []).map(option => ({ value: option, title: option }))}
              value={(answers[field.id] as string) ?? ''}
              onChange={v => setAnswer(field.id, String(v ?? ''))}
              placeholder="Elige una opción"
              error={error()}
            />
            <Show when={field.description}>
              <p class="text-xs text-foreground-subtle mt-1">{field.description}</p>
            </Show>
          </div>
        )

      case 'radio':
        return (
          <fieldset>
            <legend class="text-sm font-medium text-foreground mb-2">
              {field.label}
              <Show when={field.required}>
                <span class="text-danger"> *</span>
              </Show>
            </legend>
            <Show when={field.description}>
              <p class="text-xs text-foreground-subtle mb-2">{field.description}</p>
            </Show>
            <div class="flex flex-col gap-2">
              <For each={field.options ?? []}>
                {option => <Radio name={field.id} label={option} value={option} checked={answers[field.id] === option} onChange={() => setAnswer(field.id, option)} />}
              </For>
            </div>
            <FieldMessage error={error()} indent={false} reserve={false} />
          </fieldset>
        )

      case 'checkbox':
        return (
          <fieldset>
            <legend class="text-sm font-medium text-foreground mb-2">
              {field.label}
              <Show when={field.required}>
                <span class="text-danger"> *</span>
              </Show>
            </legend>
            <Show when={field.description}>
              <p class="text-xs text-foreground-subtle mb-2">{field.description}</p>
            </Show>
            <div class="flex flex-col gap-2">
              <For each={field.options ?? []}>
                {option => (
                  <Checkbox
                    label={option}
                    checked={Array.isArray(answers[field.id]) && (answers[field.id] as string[]).includes(option)}
                    onChange={e => toggleChoice(field.id, option, e.currentTarget.checked)}
                  />
                )}
              </For>
            </div>
            <FieldMessage error={error()} indent={false} reserve={false} />
          </fieldset>
        )

      case 'yes_no':
        return (
          <div>
            <Switch label={field.label} checked={answers[field.id] === true} onChange={e => setAnswer(field.id, e.currentTarget.checked)} />
            <Show when={field.description}>
              <p class="text-xs text-foreground-subtle mt-1">{field.description}</p>
            </Show>
            <FieldMessage error={error()} indent={false} reserve={false} />
          </div>
        )

      case 'date':
        return (
          <DateInput
            mode="date"
            label={field.label}
            required={field.required}
            helperText={field.description}
            error={error()}
            value={(answers[field.id] as string) ?? ''}
            onChange={value => setAnswer(field.id, value)}
          />
        )

      default: {
        const inputType = field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : field.type === 'phone' ? 'tel' : 'text'
        return (
          <Input
            label={field.label}
            type={inputType}
            required={field.required}
            placeholder={field.placeholder}
            helperText={field.description}
            error={error()}
            value={(answers[field.id] as string) ?? ''}
            onInput={e => setAnswer(field.id, e.currentTarget.value)}
          />
        )
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} class="space-y-5">
      <Show when={steps().length > 1}>
        <div class="space-y-2">
          <div class="flex items-center justify-between text-xs text-foreground-subtle">
            <span class="font-medium text-foreground">{currentStep()?.title}</span>
            <span>
              Paso {stepIndex() + 1} de {steps().length}
            </span>
          </div>
          <div class="h-1.5 w-full rounded-full bg-surface-muted overflow-hidden">
            <div class="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${((stepIndex() + 1) / steps().length) * 100}%` }} />
          </div>
          <Show when={currentStep()?.description}>
            <p class="text-sm text-foreground-subtle">{currentStep()?.description}</p>
          </Show>
        </div>
      </Show>

      {/* Rejilla de 6 columnas: es lo que define el ancho de cada campo. */}
      <div class="grid grid-cols-1 sm:grid-cols-6 gap-5">
        <For each={stepFields()}>{field => <div class={WIDTH_SPAN[field.width ?? 'full']}>{renderField(field)}</div>}</For>
      </div>

      <Show when={stepFields().length === 0}>
        <p class="py-8 text-center text-sm text-foreground-subtle">Este paso todavía no tiene preguntas.</p>
      </Show>

      <Show when={localError() || props.globalError}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger">{localError() || props.globalError}</div>
      </Show>

      <div class="flex items-center justify-between gap-3 pt-2">
        <Show when={stepIndex() > 0} fallback={<span />}>
          <Button type="button" variant="soft" color="default" onClick={goBack} prependIcon="material-symbols-light:arrow-back">
            Anterior
          </Button>
        </Show>

        <Show
          when={isLastStep()}
          fallback={
            <Button type="button" onClick={goNext}>
              Siguiente
            </Button>
          }
        >
          <div class="flex items-center gap-3">
            <Show when={props.preview}>
              <span class="text-xs text-foreground-subtle flex items-center gap-1">
                <Icon icon="material-symbols-light:visibility-outline" size={16} />
                Vista previa: no se envía nada
              </span>
            </Show>
            <Button type="submit" disabled={props.submitting || props.preview || !hasAnswerable()}>
              {props.submitting ? 'Enviando...' : (props.settings?.submitLabel ?? 'Enviar')}
            </Button>
          </div>
        </Show>
      </div>
    </form>
  )
}

export default FormRenderer
